import { useOutletContext } from 'react-router-dom';
import { Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useIntegrationsPermissions } from '../../hooks';
import CapabilitiesCatalogBanner from './CapabilitiesCatalogBanner.jsx';
import CapabilityGroupCard from './CapabilityGroupCard.jsx';
import { Boxes, MoreHorizontal, RefreshCw, Zap } from 'lucide-react';

const GROUPS = [
  {
    id: 'tools',
    titleType: 'tool',
    icon: Zap,
    iconClassName: 'bg-[rgba(167,139,250,0.12)] text-[#A78BFA]',
  },
  {
    id: 'knowledge',
    titleType: 'data_sync',
    icon: RefreshCw,
    iconClassName: 'bg-[rgba(5,223,114,0.10)] text-[#05DF72]',
    blurb: 'knowledge',
  },
  {
    id: 'triggers',
    titleType: 'data_sync',
    icon: RefreshCw,
    iconClassName: 'bg-[rgba(253,199,0,0.10)] text-[#FDC700]',
  },
  {
    id: 'channels',
    titleType: 'data_sync',
    icon: RefreshCw,
    iconClassName: 'bg-[rgba(6,182,212,0.10)] text-[#06B6D4]',
    blurb: 'channels',
  },
];

function chipCopy(t, { supported, isMcp, totalInstances }) {
  if (!supported) return t('integrations.provider.capabilities.notSupported');
  if (isMcp) {
    return t(
      totalInstances > 0
        ? 'integrations.provider.capabilities.mcpActivate'
        : 'integrations.provider.capabilities.mcpNoInstances',
    );
  }
  if (totalInstances === 0) return t('integrations.provider.capabilities.availablePerInstance');
  const key =
    totalInstances === 1
      ? 'integrations.provider.capabilities.activeInOne'
      : 'integrations.provider.capabilities.activeIn';
  return t(key, { active: totalInstances, total: totalInstances });
}

function itemRow(cap, { showMenu } = {}) {
  return (
    <li
      key={cap.id}
      className="grid grid-cols-1 items-center gap-1.5 border-b border-white/[0.04] px-1.5 py-2.5 last:border-b-0 sm:grid-cols-[1fr_2fr_auto] sm:gap-3"
    >
      <p className="font-mono text-xs font-semibold text-content">{cap.name}</p>
      <p className="text-xs text-content-secondary">{cap.description}</p>
      {cap.scope ? (
        <span className="w-fit justify-self-end rounded-md bg-black/[0.04] px-1.5 py-0.5 font-mono text-[11px] text-content-muted dark:bg-white/[0.04]">
          {cap.scope}
        </span>
      ) : showMenu ? (
        <MoreHorizontal
          size={16}
          strokeWidth={1.8}
          aria-hidden="true"
          className="justify-self-end text-content-muted opacity-70"
        />
      ) : (
        <span className="hidden sm:block" />
      )}
    </li>
  );
}

/**
 * Provider Detail — Capabilities tab (CT-4180). Read-only catalog matching the
 * prototype: info banner, Tool / Data Sync (knowledge · events · routing) groups,
 * optional MCP bundle. Configuration happens per instance (CT-4190) — no toggles.
 */
function CapabilitiesTab() {
  const { t } = useTranslation('admin-studio');
  const { provider, onConnect } = useOutletContext();
  const { canManageIntegrations } = useIntegrationsPermissions();

  const groups = provider.capabilityGroups ?? {};
  const instanceCount = provider.instances?.length ?? 0;

  const noticeSubtitle =
    instanceCount === 0
      ? t('integrations.provider.capabilities.noticeNoInstances')
      : t('integrations.provider.capabilities.noticeConfigure', { count: instanceCount });

  if (!provider.isConnected) {
    return (
      <div className="rounded-[10px] border border-dashed border-edge bg-surface px-6 py-12 text-center">
        <h3 className="mb-1.5 text-sm font-semibold text-content">
          {t('integrations.provider.capabilities.lockedTitle')}
        </h3>
        <p className="mb-3.5 text-xs text-content-secondary">
          {t('integrations.provider.capabilities.lockedBody')}
        </p>
        {canManageIntegrations ? (
          <Button variant="classicPrimary" size="sm" onClick={onConnect}>
            {t('integrations.provider.connect', { name: provider.name })}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <CapabilitiesCatalogBanner
        title={t('integrations.provider.capabilities.notice')}
        subtitle={noticeSubtitle}
      />

      {GROUPS.map((meta) => {
        const group = groups[meta.id];
        const supported = Boolean(group?.supported);
        const items = group?.items ?? [];
        const countLabel =
          supported && items.length > 0
            ? t('integrations.provider.capabilities.countAvailable', { count: items.length })
            : null;

        return (
          <CapabilityGroupCard
            key={meta.id}
            title={t(`integrations.card.capability.${meta.titleType}`)}
            icon={meta.icon}
            iconClassName={meta.iconClassName}
            countLabel={countLabel}
            activeChip={chipCopy(t, { supported, totalInstances: instanceCount })}
            supported={supported}
          >
            {meta.blurb ? (
              <div className="px-1.5 py-2.5">
                <p className="text-xs font-semibold text-content">
                  {t(`integrations.provider.capabilities.${meta.blurb}Title`)}
                </p>
                <p className="mt-0.5 text-xs text-content-secondary">
                  {t(`integrations.provider.capabilities.${meta.blurb}Desc`)}
                </p>
              </div>
            ) : (
              <ul>{items.map((cap) => itemRow(cap, { showMenu: meta.id === 'triggers' }))}</ul>
            )}
          </CapabilityGroupCard>
        );
      })}

      {groups.mcp?.supported ? (
        <CapabilityGroupCard
          title={t('integrations.card.capability.mcp')}
          icon={Boxes}
          iconClassName="bg-[rgba(6,182,212,0.10)] text-[#06B6D4]"
          countLabel={null}
          activeChip={chipCopy(t, { supported: true, isMcp: true, totalInstances: instanceCount })}
          supported
        >
          <p className="mb-2 px-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#06B6D4]">
            {t('integrations.provider.capabilities.mcpSubtitle', { name: provider.name })}
          </p>
          <ul className="flex flex-col gap-1.5">
            {groups.mcp.items.map((cap) => (
              <li
                key={cap.id}
                className="flex items-center gap-2.5 rounded-md bg-black/[0.03] px-2.5 py-2 dark:bg-white/[0.03]"
              >
                <p className="shrink-0 font-mono text-xs font-semibold text-content">{cap.name}</p>
                <p className="min-w-0 flex-1 text-[11px] text-content-secondary">{cap.description}</p>
                {cap.toolCount ? (
                  <Badge
                    variant="lightBlue"
                    size="small"
                    radius="full"
                    className="!border-[rgba(6,182,212,0.22)] !bg-[rgba(6,182,212,0.10)] !text-[9.5px] !font-bold !text-[#06B6D4]"
                  >
                    {t('integrations.provider.capabilities.mcpToolCount', { count: cap.toolCount })}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="mt-2 px-1.5 text-[11px] text-content-muted">
            {t('integrations.provider.capabilities.mcpHint')}
          </p>
        </CapabilityGroupCard>
      ) : null}
    </div>
  );
}

export default CapabilitiesTab;
